import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './ScheduleCfpSessionPage.module.css';
import { getTopicImage } from './cfpTopicImages';

function ChevronIcon({ expanded }) {
  return (
    <svg
      className={`${styles.subtopicChevron} ${expanded ? styles.subtopicChevronExpanded : ''}`}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AnimatedCheckmark() {
  return (
    <motion.svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
    >
      <motion.path
        d="M3.5 8.2L6.4 11.1L12.5 5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.22, ease: 'easeOut', delay: 0.04 }}
      />
    </motion.svg>
  );
}

function SubtopicRow({ label }) {
  const [expanded, setExpanded] = useState(false);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  }, []);

  return (
    <div className={styles.subtopicRow}>
      <button
        type="button"
        className={styles.subtopicButton}
        onClick={toggle}
        aria-expanded={expanded}
      >
        <span className={styles.subtopicLabel}>{label}</span>
        <ChevronIcon expanded={expanded} />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className={styles.subtopicPanelWrap}
          >
            <div className={styles.subtopicPanel}>
              <p className={styles.subtopicPanelText}>
                Your CFP will tailor guidance on <strong>{label}</strong> to your plan and timeline
                during this session.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CfpTopicCard({ topic, index, selected, disabled, onToggle, onSelect }) {
  const canToggle = !disabled || selected;
  const isToggleDisabled = disabled && !selected;
  const heroImage = getTopicImage(topic.id);

  const handleToggle = useCallback(() => {
    if (canToggle) onToggle(topic.id);
  }, [canToggle, onToggle, topic.id]);

  const handleSelect = useCallback(() => {
    onSelect(topic.id);
  }, [onSelect, topic.id]);

  return (
    <motion.article
      role="listitem"
      layout={false}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, delay: index * 0.055, ease: [0.16, 1, 0.3, 1] }}
      className={`${styles.topicCarouselSlide} ${styles.topicCard} ${
        selected ? styles.topicCardSelected : ''
      } ${disabled && !selected ? styles.topicCardDisabled : ''}`}
    >
      <div className={styles.topicCardSelectArea}>
        <button
          type="button"
          className={`${styles.topicCheckboxButton} ${
            selected ? styles.topicCheckboxButtonChecked : ''
          }`}
          onClick={handleToggle}
          aria-pressed={selected}
          aria-label={`${selected ? 'Deselect' : 'Select'} ${topic.title}`}
          disabled={isToggleDisabled}
          data-topic-toggle
        >
          <motion.span
            className={styles.topicCheckbox}
            animate={selected ? { scale: [1, 1.15, 1] } : { scale: 1 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          >
            <AnimatePresence mode="wait">
              {selected && <AnimatedCheckmark key="check" />}
            </AnimatePresence>
          </motion.span>
        </button>

        <button
          type="button"
          className={styles.topicCardToggleButton}
          onClick={handleSelect}
          aria-pressed={selected}
          disabled={false}
          data-topic-toggle
        >
          <div className={styles.topicCardHero}>
            <img
              src={heroImage}
              alt=""
              className={styles.topicCardHeroImage}
              draggable={false}
            />
          </div>

          <div className={styles.topicCardTitleArea}>
            <h3 className={styles.topicCardTitle}>{topic.title}</h3>
          </div>
        </button>
      </div>

      <div className={styles.subtopicList}>
        {topic.subtopics.map((subtopic, i) => (
          <SubtopicRow key={`${topic.id}-${i}`} label={subtopic} />
        ))}
      </div>
    </motion.article>
  );
}

export default CfpTopicCard;
